import { computed, toValue } from "vue";
import { SharedCacheHandler } from "../utils/sharedCacheHandler";
import { PLANT_ID_HEADER } from "../utils/api";
import type { CreateCustomFetchOpts, CustomFetchHandler } from "../types/api";

/**
 *
 * @param opts - Options for various stages of the fetch handling
 */
export function useCustomFetch(opts?: CreateCustomFetchOpts): CustomFetchHandler {
  const { activeFacilityId: currentPlantId, $i18n, $auth, showErrorMessage, showDevErrorMessage } = useFetchContext();

  const locale = computed(() => $i18n.locale.value);

  const CustomFetchOpts: CreateCustomFetchOpts = Object.assign(
    {
      async beforeRequest() {},
      async handleResponse(req: Request, res: Response) {
        const acceptHeader = `${req.headers.get("accept")}`.split(", ");
        const contentType = `${res.headers.get("content-type")}`.split(";")[0]!;
        if (res.status >= 500) {
          showErrorMessage("errors.generic");
        } else if (import.meta.env.VITE_SHOW_CONTENT_TYPE_WARNING) {
          if (
            res.status !== 202 && // No body with 202's
            (!req.headers.has("accept") || !acceptHeader.includes(contentType))
          ) {
            devConsole.error("accept and content-type mismatch", acceptHeader, contentType);
            devConsole.trace("accept and content-type mismatch");
            showDevErrorMessage("errors.badContentType");
          }
        }
      },
    },
    opts || {},
  );

  const meta = {
    request: new Request("null://"),
    response: new Response(),
  };

  const customFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const request = new Request(input, init);
    const headers = request.headers;
    const plantId = toValue(currentPlantId);
    // If a call already has the nexcor-plant header set, don't add another one
    if (typeof plantId === "number" && !headers.has(PLANT_ID_HEADER)) {
      headers.append(PLANT_ID_HEADER, `${plantId}`);
    }
    try {
      // stw - Should add accept header when one is not provided
      // (most APIs will be this unless returning PDF or other blob)
      if (!headers.has("Accept")) {
        headers.append("Accept", "application/json");
      }

      headers.append("Accept-Language", toValue(locale));

      if (!opts?.allowAnonymous) {
        const tokenResponse = await $auth.getToken();
        if (tokenResponse === "") {
          showErrorMessage("Expected an Authorization token, found none.");
          // Going nuclear here
          await $auth.clearData?.();
          await navigateTo("/");
        } else {
          const bearer = "Bearer " + tokenResponse;
          headers.append("Authorization", bearer);
        }
      } else {
        const isAuthed = await $auth.isAuthed();
        const token = isAuthed ? await $auth.getToken() : undefined;
        if (token) {
          headers.append("Authorization", `Bearer ${token}`);
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        if (!(await ($auth.handleAuthError?.(e) ?? Promise.resolve(false)))) {
          showErrorMessage(e.toString());
        }
      }
    }

    await CustomFetchOpts.beforeRequest!(request);
    let response = new Response();
    if (CustomFetchOpts.cache instanceof SharedCacheHandler && request.method === "GET") {
      response = await CustomFetchOpts.cache.call(request, () => fetch(request));
    } else {
      /**
       * The following statement is a compiler directive.
       *
       * nodejs undici (which is how fetch happens in node) apparently has suddenly stopped allowing us to use a request
       * object as the parameters (there is probably a dependency update that broke this) so now we have to have a split
       * here. This should never make it to production as the VITEST variable only gets used in node environments.
       */
      const promise = import.meta.env.VITEST ? fetch(input, init) : fetch(request);
      try {
        response = await promise;
      } catch (e) {
        if (e instanceof Error) {
          response = new Response(e.message, { status: 599, statusText: e.message });
        }
      }
    }
    meta.request = request;
    meta.response = response.clone();
    await CustomFetchOpts.handleResponse!(request, response.clone());
    return response.clone();
  };

  customFetch.meta = meta;

  // Wrap every fetch with a span to send info to sentry
  return customFetch;
}
